import React, { useEffect, useLayoutEffect, useState } from 'react';
import classNames from 'classnames';

import { Image } from '../../../common/components/image/image';
import Link from '../../../common/components/link/internal-link';
import withFontClassName from '../../../common/hoc/with-font-class-name';
import ReadMoreIcon from './read-more-icon';
import styles from './banner-desktop.scss';

type BannerLink = {
  to: string;
  text?: string;
};

type Props = {
  title?: React.ReactNode;
  link: BannerLink;
  image?: React.ComponentProps<typeof Image>['image'];
  category?: React.ReactNode;
  titleFontClassName?: string;
  contentFontClassName?: string;
};

// componentDidMount timing (before paint); useLayoutEffect warns during SSR
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const BannerDesktop: React.FC<Props> = ({
  title,
  link,
  image,
  category,
  titleFontClassName,
  contentFontClassName,
}) => {
  const [isReadyForAnimations, setIsReadyForAnimations] = useState(false);

  useIsomorphicLayoutEffect(() => {
    setIsReadyForAnimations(true);
  }, []);

  const renderLink = (children: React.ReactNode, className?: string) => (
    <Link className={className} addHoverClasses={false} to={link.to}>
      {children}
    </Link>
  );

  const titleClassNames = classNames(styles.title, 'blog-text-color');
  const actionClassNames = classNames(styles.action, 'blog-text-color', 'blog-icon-fill');
  const containerClassNames = classNames(
    styles.container,
    isReadyForAnimations && styles.animated,
    'blog-card-background-color',
  );

  return (
    <div className={containerClassNames}>
      <div className={styles.left}>
        <div className={classNames(styles.titleContainer, titleFontClassName)}>
          {renderLink(<h1 className={titleClassNames}>{title}</h1>)}
        </div>
        <div className={classNames(styles.actionContainer, contentFontClassName)}>
          {renderLink(
            <span className={actionClassNames}>
              {link.text}
              <ReadMoreIcon className={styles.actionIcon} />
            </span>,
          )}
        </div>
        <div className={classNames(styles.divider, 'blog-text-background-color')} />
      </div>
      {renderLink(
        <div className={styles.image}>
          <Image className={styles.imageFit} image={image} />
          <div className={classNames(styles.imageOverlay, 'blog-link-background-color')} />
          {category ? (
            <div className={classNames(styles.categoryContainer, contentFontClassName)}>
              <div className={styles.category}>
                <div className={styles.categoryTitleContainer}>
                  <p className={classNames(styles.categoryTitle, 'blog-button-primary-text-color')}>
                    {category}
                  </p>
                </div>
                <div className={styles.categoryUnderlineContainer}>
                  <div
                    className={classNames(
                      styles.categoryUnderline,
                      'blog-button-primary-text-background-color',
                    )}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>,
      )}
    </div>
  );
};

export default withFontClassName(BannerDesktop);
